import { motion } from 'framer-motion';
import { ParticleMolecule } from '@/components/ParticleMolecule';
import { Mascot } from '@/components/Mascot';

interface Props {
  onStart: () => void;
  /** Пришёл по ссылке теста (лид-магнит) — обещаем тест, а не расчёт нормы. */
  lead?: boolean;
}

/**
 * Первый экран онбординга: молекула дофамина из частиц и одна мысль бренда.
 *
 * Почему дофамин, а не просто красивая картинка: это ровно то, на чём держится
 * метод Славы. Дофамин — не «гормон удовольствия», а молекула ПРЕДВКУШЕНИЯ:
 * он растёт, когда мозг ждёт награду, и тянет сделать шаг к ней. Привычка
 * держится на этом предвкушении, а не на силе воли и запретах (правки Славы
 * 21.09.2026: «дофамин всё-таки не только про удовольствие» и 22.09.2026:
 * «дофамин — это скорее предвкушение»). Подпись «дофамин» под
 * молекулой убрана по его же решению: картинка говорит сама.
 *
 * Экран один и короткий: человек пришёл регистрироваться, и задерживать его
 * дольше одного касания нельзя.
 */
export function OnboardingIntro({ onStart, lead = false }: Props) {
  // Молекула подстраивается и под ширину, и под ВЫСОТУ экрана: под ней
  // заголовок, текст, плашка Дофи и кнопка — примерно 400 px. На маленьких
  // телефонах внутри Telegram (iPhone SE) иначе кнопка «Начать» уезжала бы
  // за нижний край.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 700;
  const size = Math.max(160, Math.min(vw - 40, 340, vh - 420));

  return (
    <div className="flex flex-col h-screen max-w-md mx-auto px-5 pb-[max(2rem,env(safe-area-inset-bottom))]">
      <div className="flex-1 flex flex-col items-center justify-center">
        <ParticleMolecule size={size} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.5 }}
        className="text-center"
      >
        <h1 className="font-display text-2xl font-bold leading-tight text-balance">
          Привычки держатся не на силе воли, а на желании повторить
        </h1>
        {/* Первая фраза называет боль аудитории (срывы) и снимает вину.
            «Я» здесь — Слава: дальше в чате онбординга пишет он, поэтому
            маскот Дофаминчик представлен в третьем лице, как проводник
            (решение Славы 21.09.2026). */}
        <p className="text-sm text-ink-muted mt-3 leading-relaxed">
          <span className="text-ink font-semibold">Если срывался — дело было не в слабости.</span>{' '}
          {lead
            ? 'Сначала тест о том, как ты ешь: 33 вопроса, 4–5 минут. Узнаешь, на что стоит обратить внимание.'
            : 'Ответь на пару вопросов, и я посчитаю твою норму. Без диет и запретов.'}
        </p>
        {/* Знакомство с маскотом (22.09.2026). Реплика в своей «плашке»
            рядом с картинкой, чтобы было видно: говорит он, а не Слава.
            Полное имя звучит один раз, дальше везде короткое «Дофи». */}
        <div className="flex items-center gap-3 mt-4 text-left rounded-2xl bg-bg-card/70 border border-line p-3">
          <Mascot pose="wave" className="w-14 h-14 shrink-0" />
          <p className="text-sm leading-snug text-ink-muted">
            Я <span className="text-emerald-glow font-semibold">Дофи</span>. Вообще-то Дофаминчик,
            но для своих просто Дофи. Я ученик Славы и буду твоим проводником.
          </p>
        </div>
        <button
          onClick={onStart}
          className="w-full mt-6 py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all"
        >
          Начать
        </button>
      </motion.div>
    </div>
  );
}
